import { Trans, useLingui } from "@lingui/react/macro";
import { useSession } from "@pontimed/auth-client";
import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { UserCircle2 } from "lucide-react";

import { useAuthModal } from "@/components/auth/auth-modal-context";
import { LocaleSwitcher } from "@/components/layout/locale-switcher";
import { GuidedSearchWizard } from "@/components/search/guided-search/guided-search-wizard";
import { Skeleton } from "@/components/ui/skeleton";
import { readFindSearch } from "@/lib/hooks/use-find-url-memory";

/**
 * Anonymous-allowed layout. Hosts /find, /provider/$slug, /facility/$slug
 * (and future /help). Both anonymous AND authenticated users see these pages
 * without redirect — booking / messaging / save are the actions that gate
 * behind login (tap-to-act pattern), not the page itself. Legal pages no
 * longer live as routes — they are overlay sheets driven by `?legal=<seg>`
 * and mounted at the root via `<LegalSheetProvider>`.
 *
 * See `_docs/web-portal-navigation.md` decisions D1, D2: portal owns the
 * functional search + interactive profiles for both Google-search visitors
 * and returning logged-in users. Apple/Google store reviewers also need to
 * reach these pages without an auth wall.
 *
 * Visual chrome mirrors `_authPages.tsx` (logo + locale switcher) so the
 * pontimed.app → portal.pontimed.app domain hop reads as the same product.
 * The right-side slot adapts to session status: "Sign in" CTA when anon,
 * account-menu link when authenticated.
 */
export const Route = createFileRoute("/_anon")({
  component: AnonLayout,
});

function AnonLayout() {
  const { status } = useSession();
  const { t } = useLingui();
  const navigate = useNavigate();
  // `/find` owns its full chrome (sticky pill header with logo + search + filters
  // + user menu) so the page is full-bleed and matches the legacy search layout
  // 1:1. All other anon routes (`/provider/$slug`, `/facility/$slug`, future
  // `/help`) keep this layout's standard chrome.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isFullBleed =
    pathname === "/find" || pathname.startsWith("/find/") || pathname === "/find-mock";

  // Logo = SPA link back to search (P24), like AppHeader's: it restores the
  // last /find query instead of reloading the whole app on "/".
  const handleLogoClick = (e: React.MouseEvent) => {
    const cached = readFindSearch();
    if (cached) {
      e.preventDefault();
      void navigate({ to: "/find", search: cached as never });
    }
  };

  return (
    <div className="text-foreground flex min-h-dvh flex-col">
      {!isFullBleed && (
        <header className="border-border/50 bg-background/80 supports-[backdrop-filter]:bg-background/70 sticky top-0 z-30 border-b pt-[env(safe-area-inset-top)] backdrop-blur-xl">
          <nav className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
            <Link
              to="/find"
              onClick={handleLogoClick}
              className="flex min-h-11 items-center"
              aria-label={t`Back to search`}
            >
              <img
                src="/icons/logo-black.png"
                alt="PontiMed"
                width={2100}
                height={450}
                className="h-8 w-auto sm:h-10 dark:hidden"
              />
              <img
                src="/icons/logo-white.png"
                alt="PontiMed"
                width={2100}
                height={450}
                className="hidden h-8 w-auto sm:h-10 dark:block"
              />
            </Link>
            <div className="flex-1" />
            <LocaleSwitcher />
            <SessionSlot status={status} />
          </nav>
        </header>
      )}
      <Outlet />
      {/* Guided-search wizard — mounted globally so any /find or profile page
          can open it via `?wizard=1&step=N`. URL is the source of truth. */}
      <GuidedSearchWizard />
    </div>
  );
}

function SessionSlot({ status }: { status: ReturnType<typeof useSession>["status"] }) {
  const { t } = useLingui();
  const auth = useAuthModal();

  if (status === "loading") {
    return <Skeleton className="h-11 w-20 rounded-lg" />;
  }

  if (status === "authenticated") {
    return (
      <Link
        to="/account/user/dashboard"
        className="text-foreground border-border bg-card hover:bg-muted/60 focus-visible:ring-primary/40 inline-flex h-11 min-w-11 items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-medium focus:outline-none focus-visible:ring-2"
        aria-label={t`Account`}
      >
        <UserCircle2 className="size-4" aria-hidden />
        <span className="hidden sm:inline">
          <Trans>Account</Trans>
        </span>
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={() => auth.open()}
      className="bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary/40 inline-flex h-11 items-center justify-center rounded-lg px-4 text-sm font-semibold focus:outline-none focus-visible:ring-2"
    >
      <Trans>Sign in</Trans>
    </button>
  );
}
